/**
 * AttachmentPicker — reusable file upload + display component (DEV-822 #126).
 *
 * Wraps the existing FileStorageController upload flow so any modal that
 * needs file attachments (HSE Report Incident, HSE Edit Incident, future
 * standard issue attachments) can drop in this component and get:
 *
 *   - Drag-drop + click-to-browse file input
 *   - Per-file upload with progress indicator
 *   - Thumbnail preview for image attachments, link preview for others
 *   - Remove button per item
 *   - Returns the current set of MinIO keys via onChange
 *
 * The parent component owns the keys state (matches React controlled-input
 * pattern). On save, it includes `attachmentFileKeys: keys` in the issue
 * create/update payload.
 *
 * Photo upload from mobile cameras works out of the box — the file input
 * uses `accept="image/*"` (with no `capture` attribute), so mobile browsers
 * offer camera or library on the same picker.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  uploadFile,
  fileNameFromKey,
  isImageByFilename,
} from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './AttachmentPicker.module.css';

// ── Props ────────────────────────────────────────────────────────

export interface AttachmentPickerProps {
  /** Current list of MinIO keys for already-attached files. */
  value: string[];
  /** Called when keys change (after upload or after remove). */
  onChange: (keys: string[]) => void;
  /**
   * Storage category (folder prefix) for uploads. HSE incidents use
   * "incidents". Standard issues could use "issues".
   */
  category?: string;
  /**
   * Optional sub-folder under category. For HSE incidents, pass the
   * project_guid to keep files organized by project.
   */
  entityId?: string;
  /** Disable upload + remove (e.g., when viewer is read-only). */
  disabled?: boolean;
  /** Restrict file types via the accept attribute. Defaults to all. */
  accept?: string;
  /** Cap on number of attachments. Defaults to 10. */
  maxFiles?: number;
}

// ── Component ────────────────────────────────────────────────────

export function AttachmentPicker({
  value,
  onChange,
  category = 'incidents',
  entityId,
  disabled = false,
  accept,
  maxFiles = 10,
}: AttachmentPickerProps) {
  const { t } = useAppTranslation('kpi');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState<string[]>([]); // names being uploaded
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (disabled) return;
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;

      const remainingSlots = maxFiles - value.length;
      if (remainingSlots <= 0) {
        setError(t('attachments.error.maxFiles', { max: maxFiles }));
        return;
      }
      const toUpload = fileArray.slice(0, remainingSlots);
      if (toUpload.length < fileArray.length) {
        setError(t('attachments.error.someSkipped', { max: maxFiles }));
      } else {
        setError(null);
      }

      setUploading((prev) => [...prev, ...toUpload.map((f) => f.name)]);
      const newKeys: string[] = [];
      for (const file of toUpload) {
        try {
          const result = await uploadFile(file, category, entityId);
          newKeys.push(result.key);
        } catch (err) {
          console.error('[attachments] upload failed', err);
          setError(
            err instanceof Error
              ? err.message
              : t('attachments.error.uploadFailed'),
          );
        } finally {
          setUploading((prev) => prev.filter((n) => n !== file.name));
        }
      }
      if (newKeys.length > 0) {
        onChange([...value, ...newKeys]);
      }
    },
    [category, entityId, value, onChange, disabled, maxFiles, t],
  );

  const handleRemove = useCallback(
    (key: string) => {
      if (disabled) return;
      onChange(value.filter((k) => k !== key));
    },
    [value, onChange, disabled],
  );

  const handleClickPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleDrop: React.DragEventHandler = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver: React.DragEventHandler = (e) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave: React.DragEventHandler = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className={styles.container}>
      {/* Existing attachments list */}
      {value.length > 0 && (
        <div className={styles.list}>
          {value.map((key) => (
            <AttachmentItem
              key={key}
              fileKey={key}
              onRemove={disabled ? undefined : handleRemove}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Currently uploading */}
      {uploading.length > 0 && (
        <div className={styles.list}>
          {uploading.map((name, idx) => (
            <div key={`uploading-${idx}-${name}`} className={styles.uploadingItem}>
              <span className={styles.spinner} aria-hidden="true" />
              <span className={styles.uploadingName}>{name}</span>
              <span className={styles.uploadingLabel}>
                {t('attachments.uploading')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone + click-to-browse */}
      {!disabled && value.length < maxFiles && (
        <div
          className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''}`}
          onClick={handleClickPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClickPicker();
            }
          }}
        >
          <span className={styles.dropZoneIcon}>+</span>
          <span className={styles.dropZoneText}>
            {t('attachments.dropZoneText', 'Drag files here or click to browse')}
          </span>
          <span className={styles.dropZoneHint}>
            {t('attachments.dropZoneHint', {
              count: value.length,
              max: maxFiles,
            })}
          </span>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={accept}
            className={styles.hiddenInput}
            onChange={(e) => {
              if (e.target.files) {
                void handleFiles(e.target.files);
                // Reset so the same file can be re-selected after a remove
                e.target.value = '';
              }
            }}
          />
        </div>
      )}

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

// ── Sub-component: one attachment row ────────────────────────────

function AttachmentItem({
  fileKey,
  onRemove,
  t,
}: {
  fileKey: string;
  onRemove?: (key: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const name = fileNameFromKey(fileKey);
  const isImage = isImageByFilename(name);
  const url = mediaSrc(fileKey) ?? '';

  return (
    <div className={styles.item}>
      {isImage ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={styles.thumbLink}
          title={t('attachments.openInNewTab', { name })}
        >
          <img src={url} alt={name} className={styles.thumb} />
        </a>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={styles.docLink}
          title={t('attachments.openInNewTab', { name })}
        >
          <span className={styles.docIcon}>📄</span>
        </a>
      )}
      <div className={styles.itemBody}>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={styles.itemName}
        >
          {name}
        </a>
      </div>
      {onRemove && (
        <button
          type="button"
          className={styles.removeBtn}
          onClick={() => onRemove(fileKey)}
          aria-label={t('attachments.remove', { name })}
          title={t('attachments.remove', { name })}
        >
          ×
        </button>
      )}
    </div>
  );
}

export default AttachmentPicker;
